// Implements: F-UIX-020, F-UIX-009
/**
 * A profile the person may read but not edit, in the full editor (audit P5, mockup v3 "Ver perfil").
 * Pure presentation.
 *
 * What the server says the person may not edit (`may_edit` false, or the system default) is shown
 * here instead of the editable form, whatever way it was opened (a list row or a link): the same
 * editor in `view` mode, with its live preview and contrast, every control disabled, a notice that
 * says why, the stored logo and reference images as images (no upload), and "Duplicar y editar" (the
 * server's instant copy, owner 2026-10-19 answer 2A) when the container offers it. No save, no
 * rename, no "Activo en", no delete.
 */

import type { ReactElement, ReactNode } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { ProfileVersionValues } from "../../../types/design";
import { ProfileEditor } from "./ProfileEditor";
import styles from "./ProfileEditor.module.css";

export interface ProfileReadOnlyProps {
  /** The system default (its own notice and logo wording) or a profile of a scope the person reads. */
  readonly system: boolean;
  readonly name: string;
  readonly currentVersion: number;
  readonly savedBy: { readonly at: string; readonly name: string | null };
  readonly values: ProfileVersionValues;
  /** The stored images, with the address the server serves each one from. */
  readonly images: readonly { readonly key: string; readonly src: string; readonly alt: string }[];
  /** The person opened it themselves: the notice takes the focus. */
  readonly focusNotice: boolean;
  /** "Duplicar y editar" (the container's instant copy); absent when the person may create nowhere. */
  readonly duplicateAction?: ReactNode;
}

const noop = (): void => undefined;

export function ProfileReadOnly({
  system,
  name,
  currentVersion,
  savedBy,
  values,
  images,
  focusNotice,
  duplicateAction,
}: ProfileReadOnlyProps): ReactElement {
  const { t } = useTranslation();
  const why = system ? t("design.readOnly.system") : t("design.readOnly.team");
  const logo = images.find((image) => image.key === "logo");
  const references = images.filter((image) => image.key !== "logo");
  return (
    <ProfileEditor
      mode="view"
      name={name}
      onNameChange={noop}
      currentVersion={currentVersion}
      savedBy={savedBy}
      values={values}
      onValuesChange={noop}
      onSubmit={noop}
      pending={false}
      notice={{ text: duplicateAction === undefined ? why : `${why} ${t("design.readOnly.duplicateHint")}`, focus: focusNotice }}
      logoSrc={logo?.src}
      logo={
        logo === undefined ? (
          <p className={styles.later}>{system ? t("design.readOnly.noLogoSystem") : t("design.readOnly.noLogo")}</p>
        ) : (
          <img className={styles.storedImage} src={logo.src} alt={logo.alt} />
        )
      }
      references={
        references.length === 0 ? undefined : (
          <ul className={styles.storedImages}>
            {references.map((image) => (
              <li key={image.key}>
                <img className={styles.storedImage} src={image.src} alt={image.alt} />
              </li>
            ))}
          </ul>
        )
      }
      referenceCount={references.length}
      duplicateAction={duplicateAction}
    />
  );
}
